import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import DataTable, { Column } from "@/components/tables/DataTable";
import TableHead from "@/components/tables/TableHead";
import StatusBadge from "@/components/badges/StatusBadge";
import { EnvelopeTableSkeletonDesktop, EnvelopeTableSkeletonMobile, ListPageHeaderSkeleton } from "@/components/skeletons";
import { useFetchListApi } from "@/utils/hooks/useFetchListApi";
import { getTemplateById, getTemplateEnvelopeListPage } from "@/services/template.service";
import { HEADINGS, TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import { PAGE_LENGTH, StatusOptions, TO_TEXT } from "@/utils/constants/constants";
import { DateFormatter, TimeFormatter, concatenateName } from "@/utils/functions/helperFunctions";

const getRecipientsText = (envelope: any) => {
    if (!envelope.recipients?.length) return "No Recipients Specified";
    return envelope.recipients
        .map((recipient: any) => recipient.full_name)
        .slice(0, 3)
        .join(", ");
};

const TemplateEnvelopeList = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [templateLoading, setTemplateLoading] = useState(true);
    const [templateName, setTemplateName] = useState("");
    const [filter, setFilter] = useState<Record<string, any>>({});

    const fetchTemplateEnvelopes = useCallback(
        (options: Parameters<typeof getTemplateEnvelopeListPage>[1] & { filters?: Record<string, any> }) => {
            if (!id) {
                return Promise.resolve({
                    data: [],
                    pagination: {
                        page: 1,
                        limit: PAGE_LENGTH,
                        total: 0,
                        totalPages: 0,
                        hasNextPage: false,
                    },
                });
            }

            return getTemplateEnvelopeListPage(id, options);
        },
        [id]
    );

    const {
        items: envelopes,
        loading,
        loadingMore,
        pagination,
        hasNextPage,
        observerRef,
        handleSearch,
        handleFilters,
        reset,
    } = useFetchListApi(fetchTemplateEnvelopes, {
        page: 1,
        limit: PAGE_LENGTH,
        sort: "created_at",
        order: "desc",
    });

    const envelopeColumns: Column<any>[] = useMemo(
        () => [
            {
                key: "subject",
                label: TABLE_HEADERS.SUBJECT,
                width: "35%",
                sortable: true,
                accessor: (envelope) => envelope.meta_data?.email_subject || "Untitled",
                render: (_, envelope, index) => (
                    <Link
                        to={`/envelope/${envelope.id}`}
                        className="user-card"
                        data-row-click-ignore="true"
                        id={`template-envelope-table-subject-${index}`}
                    >
                        <div className="nk-tnx-type w-100">
                            <div className="bg-fail-dim icon mr-2">
                                <em className="icon ni ni-file icons" id={`template-envelope-table-icon-${index}`} />
                            </div>
                            <div
                                className="data"
                                style={{
                                    marginTop: 4,
                                    maxWidth: "100%",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                <div className="info title-overflow">
                                    <div className="text-dark name">
                                        {envelope.meta_data?.email_subject || "Untitled"}
                                    </div>
                                    <div className="meta" style={{ marginTop: 4 }}>
                                        <span className="version">
                                            <span className="text-soft" id={`template-envelope-table-recipient-name-${index}`}>
                                                {TO_TEXT}: {getRecipientsText(envelope)}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Link>
                ),
            },
            {
                key: "owner",
                label: TABLE_HEADERS.OWNER,
                width: "25%",
                sortable: true,
                accessor: (envelope) => concatenateName(envelope.owner?.first_name, envelope.owner?.last_name),
                render: (value, _, index) => (
                    <span className="tb-amount" id={`template-envelope-table-owner-${index}`}>
                        {value}
                    </span>
                ),
            },
            {
                key: "status",
                label: TABLE_HEADERS.STATUS,
                width: "20%",
                accessor: (envelope) => envelope.status,
                render: (_, envelope, index) => (
                    <StatusBadge envelope={envelope} id={`${index}-template-envelope-table`} />
                ),
            },
            {
                key: "created_at",
                label: TABLE_HEADERS.CREATED_AT,
                width: "20%",
                sortable: true,
                accessor: (envelope) => envelope.created_at,
                render: (_, envelope, index) => (
                    <>
                        <span className="text-dark" id={`template-envelope-table-created-at-date-${index}`}>
                            {DateFormatter(envelope.created_at)}
                        </span>
                        <div className="meta mt-1">
                            <span className="version">
                                <span className="text-soft" id={`template-envelope-table-created-at-time-${index}`}>
                                    {TimeFormatter(envelope.created_at)}
                                </span>
                            </span>
                        </div>
                    </>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        setTemplateLoading(true);
        getTemplateById(
            id as string,
            (response: any) => {
                setTemplateName(response?.name ?? "");
                setTemplateLoading(false);
            },
            () => {
                setTemplateLoading(false);
            }
        );
    }, [id]);

    const prevTemplateIdRef = useRef(id);

    useEffect(() => {
        if (prevTemplateIdRef.current === id) return;
        prevTemplateIdRef.current = id;
        setFilter({});
        reset();
    }, [id, reset]);

    const handleApplyFilter = () => {
        handleFilters({
            dateRange: {
                startDate: filter.createdAtStart || null,
                endDate: filter.createdAtEnd || null,
            },
            status: filter.status,
        });
    };

    const handleReset = () => {
        setFilter({});
        reset();
    };

    const showHeaderSkeleton = templateLoading;
    const showTableSkeleton =
        templateLoading || loading || (envelopes.length === 0 && hasNextPage);

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                {showHeaderSkeleton ? (
                    <ListPageHeaderSkeleton showFilter />
                ) : (
                    <TableHead
                        id="template-envelope-table"
                        handleReset={handleReset}
                        pageNo={pagination.page ?? 1}
                        dataLength={pagination.limit ?? PAGE_LENGTH}
                        setFilter={setFilter}
                        filter={filter}
                        setDataLength={() => {}}
                        reset={handleReset}
                        search={handleSearch}
                        title={templateName || HEADINGS.ENVELOPES}
                        showFilter={true}
                        options={StatusOptions}
                        handleApplyFilter={handleApplyFilter}
                        currentEnvo={envelopes.length}
                        totalEnvo={pagination.total ?? 0}
                    />
                )}
            </div>
            <div>
                {showTableSkeleton ? (
                    isCompactLayout ? (
                        <EnvelopeTableSkeletonMobile />
                    ) : (
                        <table
                            className="nk-tb-list nk-tb-ulist table-weight gs-envelope-table"
                            style={{ tableLayout: "fixed", width: "100%" }}
                            aria-hidden
                        >
                            <thead>
                                <tr className="nk-tb-item nk-tb-head">
                                    <th className="nk-tb-col gs-envelope-col-subject">
                                        <span>{TABLE_HEADERS.SUBJECT}</span>
                                    </th>
                                    <th className="nk-tb-col gs-envelope-col-owner">
                                        <span>{TABLE_HEADERS.OWNER}</span>
                                    </th>
                                    <th className="nk-tb-col gs-envelope-col-status">
                                        <span>{TABLE_HEADERS.STATUS}</span>
                                    </th>
                                    <th className="nk-tb-col gs-envelope-col-created">
                                        <span>{TABLE_HEADERS.CREATED_AT}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <EnvelopeTableSkeletonDesktop />
                            </tbody>
                        </table>
                    )
                ) : (
                    <>
                        <DataTable
                            type="template_envelopes"
                            data={envelopes}
                            columns={envelopeColumns}
                            loading={false}
                            loadingMore={loadingMore}
                            hideHeaderTopBorder
                            onRowClick={(envelope) => navigate(`/envelope/${envelope.id}`)}
                        />
                        <div ref={observerRef} style={{ height: 1 }} />
                    </>
                )}
            </div>
        </div>
    );
};

export default TemplateEnvelopeList;
